/**
 * When each shift runs, for the four combinations this office actually works.
 *
 * Two shifts across two employment types: a full-timer on the 1st shift, a full-timer on the 2nd,
 * and the same for part-timers. The owner's roster sheet of 4 October 2026 gives all four, and
 * they are the defaults here.
 *
 * **These are a timetable, not a rule.** Office hours were removed on 23 September because this
 * workforce has no start time to be late for: a full-time day is nine hours and a part-time day is
 * five, whenever they are worked, and nothing in the hours engine reads a clock. What these are
 * for is the roster — the grid an employee reads to know when they are expected, in the shape of
 * the sheet that was being kept by hand.
 *
 * A shift may end after midnight (the 2nd shift ends at 00:00), which is why the end can be
 * earlier than the start and why the day cutoff has to fall after the latest end.
 */
import { EMPLOYMENT_TYPES, SHIFTS, type EmploymentType, type Shift } from './constants.js';

/** 'HH:MM' in the office timezone. */
export type ShiftWindow = { start: string; end: string };
export type ShiftTimes = Record<EmploymentType, Record<Shift, ShiftWindow>>;

/** The four windows from the owner's roster sheet, 4 October 2026. */
export const DEFAULT_SHIFT_TIMES: ShiftTimes = {
  full_time: {
    first: { start: '08:00', end: '17:00' },
    second: { start: '14:00', end: '00:00' },
  },
  part_time: {
    first: { start: '07:00', end: '12:00' },
    second: { start: '18:00', end: '23:00' },
  },
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Read a stored value into something safe to display.
 *
 * Settings are a jsonb column an admin edits, so anything missing or malformed falls back to the
 * default for that one window rather than blanking a roster: a timetable with a hole in it is
 * harder to read than one that is a little out of date, and the Settings page is where it gets
 * corrected.
 */
export function shiftTimes(value: unknown): ShiftTimes {
  const given = (value ?? {}) as Partial<Record<string, Record<string, Partial<ShiftWindow>>>>;
  const out = {} as ShiftTimes;
  for (const type of EMPLOYMENT_TYPES) {
    out[type] = {} as Record<Shift, ShiftWindow>;
    for (const shift of SHIFTS) {
      const fallback = DEFAULT_SHIFT_TIMES[type][shift];
      const got = given?.[type]?.[shift];
      out[type][shift] = {
        start: HHMM.test(String(got?.start)) ? String(got!.start) : fallback.start,
        end: HHMM.test(String(got?.end)) ? String(got!.end) : fallback.end,
      };
    }
  }
  return out;
}

/** "08:00 – 17:00", for a roster cell. */
export const shiftWindowLabel = (w: ShiftWindow): string => `${w.start} – ${w.end}`;

/** The window a given worker is expected for. */
export const windowFor = (
  times: ShiftTimes,
  type: EmploymentType | null | undefined,
  shift: Shift,
): ShiftWindow => times[type ?? 'full_time'][shift];

/**
 * The latest moment any shift ends, as minutes from midnight, counting a shift that ends after
 * midnight as the following day.
 *
 * The day cutoff has to fall after this: a worker who finishes at 00:00 and punches out at 00:05
 * against a cutoff of 23:45 has their punch-out recorded on the next day, which is how a dozen
 * second-shift days came to be auto-closed with nobody having done anything wrong.
 */
export function latestShiftEnd(times: ShiftTimes): number {
  let latest = 0;
  for (const type of EMPLOYMENT_TYPES) {
    for (const shift of SHIFTS) {
      const { start, end } = times[type][shift];
      const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
      const e = toMin(end);
      // An end at or before the start is the next day: 14:00 → 00:00 is ten hours, not minus one.
      latest = Math.max(latest, e <= toMin(start) ? e + 24 * 60 : e);
    }
  }
  return latest;
}
